import { IListingWorkGroupFigures } from "../../models/Part";
import PriceFormat from "../../utils/priceFormat";

/** How the Part numbers / Vehicles to work table is ranked. */
export type WorkGroupOrder = "mostListings" | "widestPrices";

/**
 * The gap between the group's cheapest and dearest live listing - the thing to close when pricing
 * the group together. Zero when fewer than two prices are known, so unpriced groups rank last.
 */
export function workGroupPriceSpread(group: IListingWorkGroupFigures): number {
  if (group.minPrice == null || group.maxPrice == null) return 0;
  return Math.max(0, group.maxPrice - group.minPrice);
}

/** Groups ranked by the order, with the older stock first on a tie. Only groups of two or more are kept. */
export function sortWorkGroups<T extends IListingWorkGroupFigures>(groups: T[], order: WorkGroupOrder): T[] {
  const byAge = (a: T, b: T) => (b.oldestFirstListedDays ?? 0) - (a.oldestFirstListedDays ?? 0);
  return groups
    .filter((group) => group.totalActive >= 2)
    .sort((a, b) =>
      order === "widestPrices"
        ? workGroupPriceSpread(b) - workGroupPriceSpread(a) || b.totalActive - a.totalActive || byAge(a, b)
        : b.totalActive - a.totalActive || byAge(a, b)
    );
}

/** "£40 – £90", a single figure when every listing is at the same price, or "—" when none is priced. */
export function formatPriceRange(group: Pick<IListingWorkGroupFigures, "minPrice" | "maxPrice">): string {
  if (group.minPrice == null || group.maxPrice == null) return "—";
  if (group.minPrice === group.maxPrice) return PriceFormat(group.minPrice);
  return `${PriceFormat(group.minPrice)} – ${PriceFormat(group.maxPrice)}`;
}

/** "2012 – 2016", a single year, or "" when the group has no years. */
export function formatYearSpan(group: { yearFrom: number | null; yearTo: number | null }): string {
  if (group.yearFrom == null && group.yearTo == null) return "";
  if (group.yearFrom == null || group.yearTo == null || group.yearFrom === group.yearTo) {
    return String(group.yearFrom ?? group.yearTo);
  }
  return `${group.yearFrom} – ${group.yearTo}`;
}
